import { DynamicField } from "@/types/dynamic-data";
import capitalize from "lodash/capitalize";
import defensiveCopy from "@/util/defensive-copy";
import { DynamicFieldReturnTypeV } from "@/types/dynamic-property-path";
import { isObject } from "@/util/objects";

export const isDynamicShortcode = (value: unknown): value is string => {
  return typeof value === "string" && value.startsWith("[breakdance_dynamic");
};

export function getDynamicMetaPath(path: string) {
  return `${path}_dynamic_meta`;
}

export function getFieldAndAttributesFromDynamicShortcode(shortcode: string) {
  const attributes: { [key: string]: string | undefined } = {};

  // https://stackoverflow.com/a/37577170/5993042
  shortcode.match(/[\w-]+='.*?'/g)?.forEach(function (attribute) {
    const attributeMatch = attribute.match(/([\w-]+)='(.*?)'/);

    if (attributeMatch) {
      const [, key, value] = attributeMatch;

      attributes[key] = value;
    }
  });

  // Explicitly separate field and attributes because even tho they're both shortcode attributes, we use them differently
  const field = defensiveCopy(attributes.field);
  delete attributes.field;

  return {
    field,
    attributes: attributes || {}
  };
}

export function getDynamicFieldByShortcode(
  fields: DynamicField[],
  shortcode: string
): DynamicField | undefined {
  const { field: slug } = getFieldAndAttributesFromDynamicShortcode(shortcode);

  return slug ? fields.find(field => field.slug === slug) : undefined;
}

/**
 * Converts "content.repeater_1[5].text" into "content.repeater_1[].text"
 */
export function getPathWithoutRepeaterIndex(path: string) {
  return path.replace(
    // any number inside brackets
    /\[[0-9]+\]/g,
    "[]"
  );
}

export function getDynamicPathAcceptsItems() {
  return DynamicFieldReturnTypeV.types.map(accepts => ({
    text: capitalize(accepts.value.replace("_", " ")),
    value: accepts.value
  }));
}

export function getAttributesStringFromDynamicMetaData(
  attributes: object,
  field: DynamicField
) {
  const { defaultAttributes } = field;
  const atts = { ...defaultAttributes, ...attributes };

  return Object.keys(attributes)
    .map(key => {
      const attributeKey = key as keyof typeof attributes;
      if (atts[attributeKey] === null) {
        return "";
      }
      if (typeof atts[attributeKey] === "object") {
        return Object.entries(atts[attributeKey])
          .map(childAttribute => {
            const [childKey, childValue] = childAttribute;
            if (childValue === null) {
              return "";
            }
            if (isObject(childValue)) {
              // If the child value is an object with an ID,
              // use that. Otherwise, let's just skip it to
              // prevent outputting key=[object Object]
              if (objectHasId(childValue) && childValue.id !== undefined) {
                return `${childKey}='${childValue.id}'`;
              }
              return "";
            } else if (
              typeof childValue === "string" ||
              typeof childValue === "number"
            ) {
              return `${childKey}='${childValue
                .toString()
                .replace(/'/g, "&apos;")}'`;
            }
            return "";
          })
          .join(" ");
      }
      return `${key}='${atts[attributeKey]}'`;
    })
    .reduce((previous: string, current: string) => {
      return `${previous} ${current}`;
    }, "");
}

export function getShortcodeFromDynamicField(
  attributes: object,
  field: DynamicField
) {
  const attsString = getAttributesStringFromDynamicMetaData(attributes, field);
  // Important:  parseDynamicShortcode requires single quotes for attributes
  return `[breakdance_dynamic field='${field.slug}'${attsString}]`;
}

function objectHasId(object: unknown): object is { id: string } {
  return isObject(object) && "id" in object;
}
